import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Calendar, Clock, User } from "lucide-react";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function BlogPage() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = "Blog de Revenue Management Hotelero | Zimoo";
    
    const fetchArticles = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/blog`);
        setArticles(response.data);
      } catch (error) {
        console.error("Error fetching articles:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchArticles();
  }, []);

  return (
    <div className="pt-20">
      {/* Hero */}
      <section className="section-padding bg-gradient-to-b from-[#FAFAFA] to-white grain-overlay">
        <div className="max-w-7xl mx-auto container-padding relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <span className="label-text mb-4 block">BLOG</span>
            <h1 className="heading-1 mb-6">
              Recursos de <span className="text-gradient">Revenue Management</span>
            </h1>
            <p className="body-text text-lg">
              Artículos, guías y estrategias para maximizar los ingresos de tu hotel.
            </p>
          </div>
        </div>
      </section>

      {/* Articles Grid */}
      <section className="section-padding bg-white">
        <div className="max-w-7xl mx-auto container-padding">
          {loading ? (
            <div className="text-center py-12">
              <p className="text-[#404040]">Cargando artículos...</p>
            </div>
          ) : (
            <>
              {/* Featured Article */}
              {articles.length > 0 && (
                <div className="mb-16">
                  <Link to={`/blog/${articles[0].slug}`} className="block group">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                      <div className="relative overflow-hidden rounded-2xl">
                        <img
                          src={articles[0].image}
                          alt={articles[0].title}
                          className="w-full h-[400px] object-cover transition-transform duration-500 group-hover:scale-105"
                        />
                        <div className="absolute top-4 left-4">
                          <span className="px-3 py-1 bg-[#1ABCB4] text-white text-sm rounded-full">
                            Destacado
                          </span>
                        </div>
                      </div>
                      <div>
                        <span className="text-sm text-[#1ABCB4] font-medium">{articles[0].category}</span>
                        <h2 className="heading-3 mt-2 mb-4 group-hover:text-[#1ABCB4] transition-colors">
                          {articles[0].title}
                        </h2>
                        <p className="body-text mb-6">{articles[0].excerpt}</p>
                        <div className="flex items-center gap-6 text-sm text-[#404040]">
                          <span className="flex items-center gap-2">
                            <User className="h-4 w-4" />
                            {articles[0].author}
                          </span>
                          <span className="flex items-center gap-2">
                            <Calendar className="h-4 w-4" />
                            {new Date(articles[0].date).toLocaleDateString('es-MX', { 
                              year: 'numeric', 
                              month: 'long', 
                              day: 'numeric' 
                            })}
                          </span>
                          <span className="flex items-center gap-2">
                            <Clock className="h-4 w-4" />
                            {articles[0].read_time}
                          </span>
                        </div>
                        <div className="mt-6 flex items-center gap-2 text-[#1ABCB4] font-medium group-hover:gap-3 transition-all">
                          Leer artículo <ArrowRight className="h-4 w-4" />
                        </div>
                      </div>
                    </div>
                  </Link>
                </div>
              )}

              {/* Other Articles */}
              <div className="bento-grid">
                {articles.slice(1).map((article) => (
                  <Link
                    key={article.id}
                    to={`/blog/${article.slug}`}
                    className="card-hover overflow-hidden group"
                  >
                    <div className="relative overflow-hidden">
                      <img
                        src={article.image}
                        alt={article.title}
                        className="w-full h-48 object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    <div className="p-6">
                      <span className="text-xs text-[#1ABCB4] font-medium">{article.category}</span>
                      <h3 className="text-lg font-semibold text-[#0A0A0A] mt-2 mb-3 group-hover:text-[#1ABCB4] transition-colors line-clamp-2">
                        {article.title}
                      </h3>
                      <p className="text-sm text-[#404040] mb-4 line-clamp-2">{article.excerpt}</p>
                      <div className="flex items-center justify-between text-xs text-[#404040]">
                        <span>{new Date(article.date).toLocaleDateString('es-MX', { month: 'short', day: 'numeric' })}</span>
                        <span>{article.read_time}</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      {/* Newsletter CTA */}
      <section className="section-padding bg-[#0A0A0A]">
        <div className="max-w-4xl mx-auto container-padding text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Recibe estrategias de revenue en tu inbox
          </h2>
          <p className="text-gray-400 mb-8 max-w-2xl mx-auto">
            Suscríbete a nuestro newsletter y recibe artículos, tips y recursos exclusivos para maximizar los ingresos de tu hotel.
          </p>
          <Link to="/#demo-form">
            <button className="btn-primary">
              Contáctanos
              <ArrowRight className="ml-2 h-5 w-5 inline" />
            </button>
          </Link>
        </div>
      </section>
    </div>
  );
}
